'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';

import Image from '@/components/image';
import { TIME } from '@/constants';
import { ROUTER } from '@/constants/router';
import { useOnceWhen, useInView, useDebounce } from '@/hooks';
import { ETrackerEvents, trackCustom } from '@/utils/tracker';

import type { IDrama } from '@/apis/drama-mall';

interface IForYouCardItemProps {
    item: IDrama;
    position: number;
}

export default function ForYouCardItem(props: IForYouCardItemProps) {
    const router = useRouter();
    const { item, position } = props;
    const { drama_id = '', cover_url = '', title = '', description = '', category_list = [] } = item || {};

    const { ref, inView } = useInView<HTMLDivElement>();
    useOnceWhen(inView, () => {
        trackCustom(ETrackerEvents.BOOK_SHOW, {
            book_id: drama_id,
            module_name: 'for_you',
            position,
        });
    });

    const navigateToReader = useCallback(() => {
        router.push(`${ROUTER.READER}?drama_id=${drama_id}`);
        trackCustom(ETrackerEvents.BOOK_CLICK, {
            book_id: drama_id,
            module_name: 'for_you',
            position,
        });
    }, [drama_id, position, router]);

    const handleNavigate = useDebounce(navigateToReader, TIME.NAVIGATE_DEBOUNCE, {
        leading: true,
        trailing: false,
    });

    return (
        <article ref={ref} className='flex items-center gap-[12px]' onClick={handleNavigate}>
            <div className='relative h-[107px] w-[80px] shrink-0 overflow-hidden rounded-[8px] bg-[#E5E7EB]'>
                <Image src={cover_url} />
            </div>
            <div className='flex min-w-0 flex-1 flex-col gap-[4px]'>
                <h3 className='truncate font-poppins-medium text-[14px] leading-[20px] text-black'>{title}</h3>
                <p className='line-clamp-2 text-[12px] leading-[16px] text-[#888888]'>{description}</p>
                <div className='mt-[8px] flex flex-wrap items-center gap-[4px]'>
                    {category_list.slice(0, 2).map(tag => (
                        <span
                            key={tag}
                            className='rounded-[4px] bg-[#ECE7F7] px-[6px] py-[2px] text-[12px] leading-[16px] text-[#816AAE]'
                        >
                            {tag}
                        </span>
                    ))}
                </div>
            </div>
        </article>
    );
}
